import React, { useState, createContext, useContext } from 'react';
import { Menu } from 'lucide-react';
import { Sidebar } from '../Sidebar';
import { DashboardTopHeader } from '../dashboard/DashboardTopHeader';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface SidebarContextType {
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (value: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebarCollapse = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebarCollapse must be used within DashboardLayout');
  }
  return context;
};

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  return (
    <SidebarContext.Provider value={{ isSidebarCollapsed, setIsSidebarCollapsed }}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Top Header - Full width across entire screen */}
        <DashboardTopHeader />

        <div className="flex pt-16">
          {/* Sidebar */}
          <Sidebar
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            setIsSidebarCollapsed={setIsSidebarCollapsed}
          />

          {/* Main content area */}
          <div className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
            {/* Mobile Menu Button */}
            <div className="md:hidden fixed top-20 left-4 z-50">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700"
              >
                <Menu className="h-6 w-6 text-gray-600 dark:text-gray-300" />
              </button>
            </div>

            <main className="p-4 md:p-8">
              {children}
            </main>
          </div>

          {/* Mobile menu overlay */}
          {isMobileMenuOpen && (
            <div
              className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}
        </div>
      </div>
    </SidebarContext.Provider>
  );
};
